import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  Currency,
  PaymentMethodProvider,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandL,
  BrandM,
  HeadlineXL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import {
  MobileAppFeature,
  NotMobileAppFeature,
} from '@sorare/core/src/components/BuyableFeature';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary, payment } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LazyPaymentProvider } from 'components/buyActions/LazyPaymentProvider';
import { Attribute } from 'components/buyActions/PaymentBox/CardOverview';
import { usePollInGameCurrencyPackDelivery } from 'hooks/primaryBuy/usePollInGameCurrencyPackDelivery';

import type { GemsPack_inGameCurrencyPack } from './__generated__/index.graphql';
import gems from './assets/gems.png';

const Root = styled(Vertical).attrs({ gap: 2 })<{
  background: string;
}>`
  position: relative;
  border-radius: var(--double-unit);
  padding: var(--quadruple-unit) var(--double-unit) var(--double-unit);
  justify-content: space-between;
  text-align: center;
  background:
    linear-gradient(180deg, rgba(16, 16, 16, 0) 58.7%, #101010 100%),
    no-repeat top / contain url(${props => props.background});

  background-size: contain;
  background-position: top;
  aspect-ratio: 296 / 368;
`;

const Container = styled(Vertical).attrs({ center: true, gap: 2 })`
  flex-grow: 1;
  justify-content: flex-end;
`;

const BonusRibon = styled(Horizontal).attrs({ gap: 0.5 })`
  position: absolute;
  top: var(--double-unit);
  right: calc(var(--unit) * -1);
  border-top-left-radius: var(--double-unit);
  border-top-right-radius: var(--half-unit);
  border-bottom-left-radius: var(--double-unit);
  background: #ad00ff;
  padding: var(--half-unit) var(--unit);
  &:after {
    content: '';
    position: absolute;
    bottom: calc(var(--unit) * -1);
    right: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: var(--unit) var(--unit) 0 0;
    border-color: #ad00ff80 transparent transparent transparent;
  }
`;

const Leftcolumn = styled(Horizontal).attrs({ gap: 2 })`
  background: linear-gradient(to bottom, #8ce456 0%, #096709 100%) border-box;
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  z-index: 0;
  padding: var(--double-unit);

  img {
    max-width: 100px;
  }

  @media ${laptopAndAbove} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    img {
      max-width: none;
    }
  }
`;

const AttributeWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  border-radius: var(--double-unit);
  background-color: rgba(var(--c-rgb-white), 0.1);
`;

const Value = styled(AttributeWrapper)`
  padding: var(--half-unit) var(--unit);
  img {
    width: 24px;
  }
`;

const DialogBody = styled(Vertical)`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: var(--double-unit);
  padding: 0 var(--triple-unit) var(--triple-unit) var(--triple-unit);
  @media ${laptopAndAbove} {
    width: 444px;
  }
`;

const DialogContent = styled(Vertical)`
  padding: var(--double-unit);
  align-items: center;
  img {
    width: 200px;
    max-width: 100%;
  }
`;

const DialogGemsContainer = styled(Horizontal).attrs({ gap: 1 })`
  align-items: center;
  img {
    max-width: 100px;
  }
`;

type Props = {
  gemsPack: GemsPack_inGameCurrencyPack;
  onClose: () => void;
};

export const GemsPack = ({ gemsPack, onClose }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const [dialogOpened, setDialogOpened] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const { currentUser } = useCurrentUserContext();
  const [currentUserBalanceGemsBalance] = useState(
    currentUser?.gemsBalances[0]?.amount || 0
  );
  const { postMessage } = useIsMobileApp();

  const track = useEvents();

  const {
    flags: { commonCardMonetization = false },
  } = useFeatureFlags();

  const onPaymentSucceeded = (success: boolean) => {
    if (success) setPaymentSucceeded(true);
  };

  const { polling, startPolling } = usePollInGameCurrencyPackDelivery(
    currentUserBalanceGemsBalance,
    onPaymentSucceeded
  );
  const price = toMonetaryAmount(gemsPack.price);
  const { main } = useAmountWithConversion({ monetaryAmount: price });
  if (!commonCardMonetization || price.isZero) return null;

  return (
    <>
      <Root background={gemsPack.pictureUrl}>
        <Container>
          {gemsPack.bonusAmount > 0 && (
            <BonusRibon>
              <GemIcon size={2} />
              <LabelS bold>
                <FormattedMessage
                  id="GemsPack.bonusAmount"
                  defaultMessage="+{amount} bonus"
                  values={{ amount: gemsPack.bonusAmount }}
                />
              </LabelS>
            </BonusRibon>
          )}
          <Horizontal>
            <GemIcon size={2} />
            <BrandM>x{gemsPack.baseAmount}</BrandM>
          </Horizontal>
        </Container>
        <MobileAppFeature exceptMarketAuthorizedIosApp>
          <Button
            size="medium"
            onClick={() => {
              track('Click Gem Package', {
                eurAmount: price.eurCents,
                gemAmount: gemsPack.baseAmount,
              });
              postMessage('buyInGameCurrencyPack', {
                packId: gemsPack.id,
              });
            }}
          >
            {main}
          </Button>
        </MobileAppFeature>
        <NotMobileAppFeature exceptMarketAuthorizedIosApp>
          <Button
            size="medium"
            onClick={() => {
              track('Click Gem Package', {
                eurAmount: price.eurCents,
                gemAmount: gemsPack.baseAmount,
              });

              setDialogOpened(true);
            }}
          >
            {main}
          </Button>
        </NotMobileAppFeature>
      </Root>
      {!!dialogOpened && (
        <LazyPaymentProvider
          paymentProps={{
            canChangeRefCurrency: true,
            onSuccess: () => startPolling(),
            onSubmit: async () => Promise.resolve(),
            cta: payment.confirmAndPay,
            canUseConversionCredit: false,
            currencies: [Currency.FIAT],
            sport: Sport.FOOTBALL,
            onlyCreditCards: true,
            useKlarna: false,
            externalPaymentProvider: PaymentMethodProvider.STRIPE,
            objectId: gemsPack.id,
            objectType: 'inGameCurrencyPack',
            price,
          }}
          paymentBoxProps={{
            loadingPolling: polling,
          }}
          paymentDialogProps={{
            onClose: () => {
              setDialogOpened(false);
            },
            title: (
              <HeadlineXL as="h5">
                <FormattedMessage {...payment.paymentBoxTitle} />
              </HeadlineXL>
            ),
            tokenPreview: (
              <Leftcolumn>
                <Img src={gems} width="320" height="290" alt="" />
                <Vertical gap={0.5}>
                  <Attribute
                    label={
                      <FormattedMessage
                        id="GemsPack.previewBaseAmount"
                        defaultMessage="Bundle of Gems"
                      />
                    }
                    value={
                      <Value>
                        +{gemsPack.baseAmount} <GemIcon />
                      </Value>
                    }
                  />
                  {gemsPack.bonusAmount > 0 && (
                    <Attribute
                      label={
                        <FormattedMessage
                          id="GemsPack.previewBonusAmount"
                          defaultMessage="Bonus"
                        />
                      }
                      value={
                        <Value>
                          +{gemsPack.bonusAmount} <GemIcon />
                        </Value>
                      }
                    />
                  )}
                </Vertical>
              </Leftcolumn>
            ),
          }}
        />
      )}
      {!!paymentSucceeded && (
        <Dialog open maxWidth="xs" hideHeader onClose={onClose}>
          <DialogBody>
            <DialogContent>
              <DialogGemsContainer>
                <GemIcon size={8} />
              </DialogGemsContainer>
              <BrandL>
                <FormattedMessage
                  id="GemsPack.confirmationTitle"
                  defaultMessage="You've gained {amount} gems"
                  values={{
                    amount: gemsPack.baseAmount + gemsPack.bonusAmount,
                  }}
                />
              </BrandL>
              <LabelM>
                <FormattedMessage
                  id="GemsPack.confirmation"
                  defaultMessage="You can now spend your gems in the Daily Deal section in the Market"
                />
              </LabelM>
            </DialogContent>
            <Button size="medium" color="quaternary" onClick={() => onClose()}>
              <FormattedMessage {...glossary.continue} />
            </Button>
          </DialogBody>
        </Dialog>
      )}
    </>
  );
};

GemsPack.fragments = {
  inGameCurrencyPack: gql`
    fragment GemsPack_inGameCurrencyPack on InGameCurrencyPack {
      id
      baseAmount
      bonusAmount
      pictureUrl
      price {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<GemsPack_inGameCurrencyPack>,
};
